import { html, type TemplateResult } from "lit";
import { t } from "../../i18n/index.ts";

export type PairingRequest = {
  id: string;
  code: string;
  createdAt: string;
  lastSeenAt: string;
  meta?: Record<string, string>;
};

export type PairingState = {
  requests: PairingRequest[];
  loading: boolean;
  approving: string | null;
};

export type PairingPanelParams = {
  channel: string;
  channelName: string;
  state: PairingState;
  onApprove: (code: string) => void;
  onRefresh: () => void;
};

export function renderPairingPanel(params: PairingPanelParams): TemplateResult {
  const { channel, channelName, state, onApprove, onRefresh } = params;

  return html`
    <div class="panel">
      <div class="panel-header">
        <div style="display: flex; align-items: center; gap: 12px;">
          <h2 style="margin: 0;">${t("pairing.title")} - ${channelName}</h2>
          <button
            class="btn"
            @click=${onRefresh}
            ?disabled=${state.loading}
            style="padding: 6px 12px;"
          >
            ${state.loading ? t("pairing.refreshing") : t("pairing.refresh")}
          </button>
        </div>
        <p style="margin: 8px 0 0 0; color: var(--text-muted); font-size: 14px;">
          ${t("pairing.description", { channel: channelName })}
        </p>
      </div>

      <div class="panel-body">
        ${state.loading && state.requests.length === 0
          ? html`<div style="padding: 24px; text-align: center; color: var(--text-muted);">
              ${t("pairing.loading")}
            </div>`
          : state.requests.length === 0
            ? html`<div style="padding: 24px; text-align: center; color: var(--text-muted);">
                ${t("pairing.noPending")}
              </div>`
            : html`
                <table style="width: 100%; border-collapse: collapse;">
                  <thead>
                    <tr style="border-bottom: 1px solid var(--border);">
                      <th style="padding: 12px; text-align: left; font-weight: 600;">${t("pairing.code")}</th>
                      <th style="padding: 12px; text-align: left; font-weight: 600;">${t("pairing.userId")}</th>
                      <th style="padding: 12px; text-align: left; font-weight: 600;">${t("pairing.requested")}</th>
                      <th style="padding: 12px; text-align: left; font-weight: 600;">${t("pairing.action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${state.requests.map(
                      (req) => html`
                        <tr style="border-bottom: 1px solid var(--border);">
                          <td style="padding: 12px; font-family: monospace; font-weight: 600;">
                            ${req.code}
                          </td>
                          <td style="padding: 12px; font-family: monospace; font-size: 13px;">
                            ${req.id}
                          </td>
                          <td style="padding: 12px; color: var(--text-muted); font-size: 13px;">
                            ${new Date(req.createdAt).toLocaleString()}
                          </td>
                          <td style="padding: 12px;">
                            <button
                              class="btn primary"
                              @click=${() => onApprove(req.code)}
                              ?disabled=${state.approving === req.code}
                              style="padding: 6px 16px; font-size: 13px;"
                            >
                              ${state.approving === req.code ? t("pairing.approving") : t("pairing.approve")}
                            </button>
                          </td>
                        </tr>
                      `,
                    )}
                  </tbody>
                </table>
              `}
      </div>
    </div>
  `;
}
